<template>
  <modal
    v-if="active"
    height="720px"
    width="620px"
    :title="reportTitle"
    bodyPadding="0"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/shared-report`"
  >
    <template #default>
      <div class="form-container">
        <ui-info class="mb-16" v-if="isTreeView">
          {{ t('All the reports will be shared in tableview.') }}
        </ui-info>
        <div>
          <ui-input
            v-model="form.name"
            autofocus
            name="name"
            :label="`${t('Name of Report')}*`"
            :placeholder="t('Name of Report')"
            type="text"
            :error-messages="errorsDict['name']"
          ></ui-input>
          <ui-period-picker-input
            :value="{
              from: form.reportPeriodStart,
              to: form.reportPeriodEnd,
              timezone: form.timezone,
            }"
            :dateValue="form.date"
            @update:dateValue="form.date = $event"
            :start-timestamp-errors="errorsDict['startTimestamp']"
            :end-timestamp-errors="errorsDict['endTimestamp']"
            time-in-title
            :withTimeMenu="true"
            :hide-details="2"
            @input="handleUpdatePeriod"
          >
            <template #label>
              <div class="report-container pb-4">
                <ui-label
                  :text="t('Period of Shared Report')"
                  :help-tooltip="t('Date and time for which the report will be shared')"
                />
                <ui-checkbox
                  v-model="form.showDate"
                  :label="t('Show Date and Time in Shared Report')"
                />
              </div>
            </template>
          </ui-period-picker-input>
          <div>
            <ui-date-time-picker
              :withTimeFilter="true"
              :value="form.availableUntil"
              :disabled="form.disableAvailableUntil"
              @input="handleUpdateAvailableUntil"
              :maxDate="maxDate()"
              :minDate="new Date()"
              ref="dateTimePicker"
            >
              <template #label>
                <ui-label
                  :text="t('Available Until')"
                  :help-tooltip="t('How long the generated report will be available')"
                />
                <ui-checkbox
                  class="disable-available-until"
                  v-model="form.disableAvailableUntil"
                  :label="t('No Time Limits')"
                />
              </template>
            </ui-date-time-picker>
            <span class="label">{{ t('Max 60 Days') }}</span>
          </div>
        </div>
        <div class="columns-container">
          <div class="header justify-space-between pb-4">
            <div class="d-flex align-items-center">
              <ui-icon icon="fas fa-eye"></ui-icon>
              <div class="show-columns ml-4">
                {{ t('Show Columns') }}
              </div>
            </div>

            <ui-checkbox
              v-model="selectAll"
              @update:model-value="toggleSelectAll"
              :label="t('Select All')"
            ></ui-checkbox>
          </div>
          <ui-input
            v-model="searchColumn"
            prepend-inner-icon="fas fa-search"
            :placeholder="t('Search')"
            :hide-details="1"
          />
          <div v-show="!!filteredColumns.length" class="columns-wrapper">
            <div class="columns">
              <div class="cell" v-for="column in filteredColumns" :key="column.value">
                <ui-checkbox
                  :planLimits="column.planLimits"
                  class="mt-4"
                  v-model="columns[column.value]"
                  :disabled="column.disabled"
                  @update:model-value="handleToggle(column.value, $event)"
                  :label="column.text"
                ></ui-checkbox>
              </div>
            </div>
          </div>
          <div v-show="!filteredColumns.length" class="ui-menu-content__list-item-container not-found">
            <ui-icon :color="$colors.gray20" icon="fa fa-search-minus"></ui-icon>
            <span class="ui-menu-content__list-item-text ml-2">{{ t('Not found') }}</span>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <section class="justify-space-between align-items-center xs12" style="display: flex; width: 100%">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive"></error-aggregator>
        </div>
        <div class="d-flex justify-end">
          <ui-update-info v-if="data.formData?.id" :data="updateData" infoFormat="short"></ui-update-info>
          <ui-button
            icon="fas fa-times"
            :text="t('Cancel')"
            @click="checkClose"
          ></ui-button>
          <ui-button
            button-style="accent"
            :icon="`fas ${data.action === 'edit' ? 'fa-check' : 'fa-link'}`"
            :text="submitButtonText"
            @click="submit"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { Router } from 'vue-router';
import _ from 'lodash';
import md5 from 'md5';
import moment from 'moment-timezone';
import {
  getReportColumnPlanLimits,
  useApiFieldErrors,
  useColors,
  useI18n,
  useMixpanel,
  usePeriodPickerRange,
  useReportFilters,
  useReportMetrics,
  useReportParams,
  useTableQuery,
} from '@/composables';
import { createReportFiltersApi } from '@/views/reports/report-filters-bindings-runtime';
import { $emitter } from '@/services/emitter';
import {
  postSharedReportApiRequest,
  putSharedReportApiRequest,
} from '@/services/api/shared-reports-api';
import { TagsLogicalRelationEnum } from '@/services/models/Enums';
import { useStore } from '@/store';
import { resolveReportTypeFromRoute } from '@/views/reports/report-route-behavior';

type AppRouter = Router & {
  getPageTitle: (route?: unknown) => string;
};

type ShareReportDialogData = {
  formData?: {
    id: string;
    name: string;
    availableUntil: string;
    status: string;
    timezone: string;
    queryConditions: {
      columns: string | string[];
      columnsType: string;
      status: string;
      groupBy: string | string[];
      date: string;
      from: string;
      to: string;
      filters: object;
      workspaceIds: string[];
      orderBy: string;
      orderDir: string;
      currency: string;
      search?: string | string[];
      searchBy?: string;
      textLogicalRelation?: string;
      tags?: string[];
      tagsLogicalRelation?: string;
      marks?: Array<string | number>;
      marksFilterMode?: string;
      havingFilters?: ShareReportHavingFilter[];
    };
    created: string;
    updated: string;
    updatedBy: string;
    showDate: boolean;
  };
  action?: 'edit' | 'duplicate';
};

type ShareReportHavingFilter = {
  table: string;
  option: string;
  value: number;
};

type ShareReportFormFields = {
  reportPeriodEnd: string | null;
  reportPeriodStart: string | null;
  timezone: string;
  name: string;
  showDate: boolean;
  availableUntil?: string | null;
  disableAvailableUntil?: boolean;
  columnsType?: string;
  status: string | null;
  columnsStatus: string | null;
  date: string;
  groupBy: string | string[];
  search?: string | string[];
  searchBy?: string;
  marks?: Array<string | number>;
  marksFilterMode?: string;
  tags?: string[];
  tagsLogicalRelation?: TagsLogicalRelationEnum | string;
  textLogicalRelation?: TagsLogicalRelationEnum | string;
  havingFilters?: ShareReportHavingFilter[];
  updated?: string;
  created?: string;
  updatedBy?: string;
  filters?: object;
  workspaceIds: string[];
  orderBy?: string;
  orderDir?: string;
  currency?: string | null;
};

const filledFilterValue = (value: unknown) => {
  if (value == null || value === '') return false;
  return !Array.isArray(value) || value.length > 0;
};

const reportFilterConditions = (source: {
  search?: unknown;
  searchBy?: unknown;
  textLogicalRelation?: unknown;
  tags?: unknown;
  tagsLogicalRelation?: unknown;
  marks?: unknown;
  marksFilterMode?: unknown;
  filterHaving?: unknown;
  filterHavingOptions?: unknown;
  filterHavingValues?: unknown;
  havingFilters?: ShareReportHavingFilter[];
} | null | undefined) => {
  if (!source) return {};

  const searchBy = typeof source.searchBy === 'string' ? source.searchBy : '';
  const conditions: Pick<
    ShareReportFormFields,
    | 'search'
    | 'searchBy'
    | 'textLogicalRelation'
    | 'tags'
    | 'tagsLogicalRelation'
    | 'marks'
    | 'marksFilterMode'
    | 'havingFilters'
  > = {};

  if ((searchBy === 'text' || (!searchBy && filledFilterValue(source.search))) && filledFilterValue(source.search)) {
    conditions.searchBy = 'text';
    conditions.search = source.search as string | string[];
    if (filledFilterValue(source.textLogicalRelation)) {
      conditions.textLogicalRelation = String(source.textLogicalRelation);
    }
  }

  if (searchBy === 'tags' && filledFilterValue(source.tags)) {
    conditions.searchBy = 'tags';
    conditions.tags = source.tags as string[];
    if (filledFilterValue(source.tagsLogicalRelation)) {
      conditions.tagsLogicalRelation = String(source.tagsLogicalRelation);
    }
  }

  if ((searchBy === 'marks' || searchBy.startsWith('marks-')) && filledFilterValue(source.marks)) {
    conditions.searchBy = 'marks';
    conditions.marks = source.marks as Array<string | number>;
    conditions.marksFilterMode = String(
      source.marksFilterMode || (searchBy.includes('exclude') ? 'exclude' : 'include'),
    );
  }

  if (Array.isArray(source.havingFilters) && source.havingFilters.length) {
    conditions.havingFilters = source.havingFilters;
  } else if (Array.isArray(source.filterHaving) && source.filterHaving.length) {
    const options = Array.isArray(source.filterHavingOptions) ? source.filterHavingOptions : [];
    const values = Array.isArray(source.filterHavingValues) ? source.filterHavingValues : [];
    const havingFilters = source.filterHaving
      .map((table, index) => ({
        table: String(table),
        option: String(options[index] ?? ''),
        value: Number(values[index]),
      }))
      .filter((item) => (
        item.table
        && ['greater', 'lower', 'equals'].includes(item.option)
        && Number.isFinite(item.value)
      ));
    if (havingFilters.length) conditions.havingFilters = havingFilters;
  }

  return conditions;
};

const normalizeSelectedColumns = (columnsValue: string | string[] | undefined): string[] => {
  if (!columnsValue) return [];
  if (Array.isArray(columnsValue)) return columnsValue;
  return [columnsValue];
};

const props = withDefaults(
  defineProps<{
    data?: ShareReportDialogData;
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const { t, locale } = useI18n();
const $colors = useColors();
const store = useStore();
const route = useRoute();
const router = useRouter() as AppRouter;
const { trackMixpanel } = useMixpanel();

const reportFilters = useReportFilters();
const reportMetrics = useReportMetrics();
const reportParamsApi = useReportParams();
const tableQueryApi = useTableQuery();
const { getRangeValueCollection } = usePeriodPickerRange('YYYY-MM-DD HH:mm:ss');

const {
  errors,
  errorsControlActive,
  errorsDict,
  clearErrors,
  applyApiErrors,
} = useApiFieldErrors();

let reportFiltersApi: ReturnType<typeof createReportFiltersApi> | null = null;

const getReportFiltersApi = () => {
  if (!reportFiltersApi) {
    reportFiltersApi = createReportFiltersApi(
      reportFilters,
      () => ({
        getType: () => resolveReportTypeFromRoute(route.name, route.params.type as string) as string,
        getGrouping: () => undefined,
        getSelectedMetrics: () => [],
        getRouteName: () => route.name,
      }),
    );
  }

  return reportFiltersApi;
};

const getFiltersFromApi = () => getReportFiltersApi().getFilters();

const active = ref(false);
const searchColumn = ref('');
const columns = ref<Record<string, boolean>>({});
const selectAll = ref(false);
const disabledColumns = ref<Record<string, boolean>>({});
const oldHash = ref('');
const form = ref<ShareReportFormFields>({
  reportPeriodStart: null,
  reportPeriodEnd: null,
  timezone: '',
  name: '',
  showDate: true,
  availableUntil: null,
  disableAvailableUntil: false,
  date: 'today',
  status: null,
  columnsStatus: null,
  groupBy: [],
  filters: {},
  workspaceIds: [],
  currency: null,
});

const storeColumns = computed(() =>
  resolveReportTypeFromRoute(route.name, route.params.type as string),
);

const isTreeView = computed(() => {
  const reportParams = reportParamsApi.getReportParams();
  const tableQuery = tableQueryApi.getTableQueryForCurrentTab();
  return reportParams.view === 'tree' && (tableQuery.treeLevels as string[])?.some((item) => item);
});

const groupBy = computed(() => {
  const reportParams = reportParamsApi.getReportParams();
  const arrayGroup = Array.isArray(reportParams.groupBy)
    ? reportParams.groupBy
    : [reportParams.groupBy];
  const treeLevels = (tableQueryApi.getTableQueryForCurrentTab().treeLevels as string[]) || [];
  return isTreeView.value ? [...arrayGroup, ...treeLevels] : arrayGroup;
});

const filters = computed(() => {
  const selectedItems = tableQueryApi.tableSelectedItems.value;
  const initialFilters = getFiltersFromApi();
  const result: Record<string, unknown>[] = [];

  const mapKeyFilter = {
    filterOptions: 'option',
    filterTables: 'table',
    filterValues: 'value',
  };

  const arrayFilters = Object.entries(
    _.pick(initialFilters, ['filterOptions', 'filterTables', 'filterValues']),
  );
  const selectedIds = (selectedItems as { DT_RowId: string }[]).map((item) => item.DT_RowId);

  if (arrayFilters.length) {
    const mappedFilters = arrayFilters.reduce<Record<string, unknown>>((accumulator, [key, value]) => {
      if (key === 'filterTables') {
        accumulator[mapKeyFilter[key]] = (value as string[])[0];
      } else if (key === 'filterValues') {
        accumulator[mapKeyFilter[key]] = (value as string[])[0].split('^');
      } else {
        accumulator[mapKeyFilter[key as keyof typeof mapKeyFilter]] = value;
      }
      return accumulator;
    }, {});
    result.push(mappedFilters);
  }

  if (selectedIds.length) {
    result.push({
      value: selectedIds,
      table: groupBy.value[0],
      option: 'equals',
    });
  }

  return result;
});

const columnsList = computed(() => {
  const { columnsType } = form.value;
  return reportMetrics.getMetricsList(route.name as string, columnsType).map((item) => ({
    ...item,
    planLimits: getReportColumnPlanLimits(item),
  }));
});

watchEffect(() => {
  columnsList.value.forEach((item) => {
    if (item.disabled) {
      disabledColumns.value[item.value] = true;
      columns.value = { ...columns.value, [item.value]: true };
    }
  });
});

const selectedColumns = computed(() =>
  Object.entries(columns.value).reduce<string[]>((accumulator, [key, value]) => {
    if (value) {
      accumulator.push(key);
    }
    return accumulator;
  }, []),
);

const filteredColumns = computed(() => {
  const query = (searchColumn.value || '').toLowerCase();
  return columnsList.value.filter((column) => {
    const label = (
      column.text ||
      reportMetrics.reportMetricsName(column.value) ||
      column.value ||
      ''
    )
      .toString()
      .toLowerCase();
    return label.includes(query);
  });
});

const reportTitle = computed(() => {
  switch (props.data?.action) {
    case 'duplicate':
      return t('Duplicate Shared Report');
    case 'edit':
      return t('Edit Shared Report');
    default:
      return t('Create Shared Report');
  }
});

const submitButtonText = computed(() => {
  switch (props.data?.action) {
    case 'duplicate':
      return t('Create Link');
    case 'edit':
      return t('Save');
    default:
      return t('Create Link');
  }
});

const updateData = computed(() => {
  const { updated, created } = form.value;
  return { updated, created };
});

const defaultForm = () => ({
  name: (tableQueryApi.getTableSelectedItem().name as string) || '',
  availableUntil: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  showDate: true,
});

const formatDateLocal = (date: string) =>
  moment(moment.utc(date).toDate()).local().format('YYYY-MM-DD HH:mm:ss');

const dateToUtc = (date: string) => moment(date).utc().format('YYYY-MM-DD HH:mm:ss');

const getCurrentHash = () => md5(JSON.stringify({ ...form.value, ...columns.value }));

const close = () => {
  emit('close');
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => close() },
    });
  } else {
    close();
  }
};

const handleUpdateAvailableUntil = (date: { format: (format: string) => string }) => {
  form.value.availableUntil = date.format('YYYY-MM-DD HH:mm:ss');
};

const maxDate = () => moment().add(59, 'days').valueOf();

const handleUpdatePeriod = ({ from, to, timezone }: TimeRange) => {
  form.value.reportPeriodStart = from;
  form.value.reportPeriodEnd = to;
  form.value.timezone = timezone;
};

const setSelectedColumns = (selected: string | string[]) => {
  const normalizedColumns = normalizeSelectedColumns(selected);
  columns.value = columnsList.value.reduce<Record<string, boolean>>((accumulator, { value }) => {
    accumulator[value] = normalizedColumns.includes(value);
    return accumulator;
  }, {});
};

const toggleSelectAll = (value: unknown) => {
  const checked = value === true;
  columnsList.value.forEach(({ value: columnValue }) => {
    if (disabledColumns.value[columnValue]) return;
    columns.value[columnValue] = checked;
  });
};

const handleToggle = (key: string, value: unknown) => {
  columns.value[key] = value === true;
  selectAll.value = selectedColumns.value.length === Object.keys(columns.value).length;
};

const submit = async () => {
  clearErrors();

  const {
    name,
    availableUntil,
    disableAvailableUntil,
    reportPeriodStart,
    reportPeriodEnd,
    timezone,
    columnsType,
    status,
    columnsStatus,
    groupBy: formGroupBy,
    filters: formFilters,
    showDate,
    workspaceIds,
    orderBy,
    orderDir,
    currency,
    date,
  } = form.value;
  const userId = store.user.id;
  const currencyId = store.user.currencyId;

  const payload = {
    userId,
    name,
    availableUntil: disableAvailableUntil ? null : dateToUtc(availableUntil as string),
    status,
    showDate,
    timezone,
    queryConditions: {
      columnsType,
      status: columnsStatus,
      from: reportPeriodStart,
      to: reportPeriodEnd,
      columns: selectedColumns.value,
      currency: currency || currencyId,
      groupBy: formGroupBy,
      filters: formFilters,
      date,
      workspaceIds,
      ...reportFilterConditions(form.value),
      ...(selectedColumns.value.includes(orderBy as string) && {
        orderBy,
        orderDir,
      }),
    },
  };

  try {
    if (props.data?.action === 'edit' && props.data.formData?.id) {
      await putSharedReportApiRequest(props.data.formData.id, payload);
      $emitter.emit('load-shared-reports');
      close();
      $emitter.emit('toast-success', t('Shared report successfully updated'));
      return;
    }

    const result = (await postSharedReportApiRequest(payload)) as { id: string; name: string };
    const { id, name: reportName } = result;
    close();

    if (props.data?.action === 'duplicate') {
      $emitter.emit('load-shared-reports');
    }
    $emitter.emit('open-dialog', { name: 'CreateLinkDialog', data: { id, name: reportName } });
    $emitter.emit('toast-success', t('Shared report successfully created'));

    trackMixpanel('shared_report_create', {
      page_title: router.getPageTitle(),
    });
  } catch (error) {
    applyApiErrors(error);
  }
};

onMounted(() => {
  if (!_.isEmpty(props.data?.formData)) {
    const {
      name,
      queryConditions,
      availableUntil,
      status,
      created,
      updated,
      updatedBy,
      showDate,
      timezone,
    } = props.data.formData!;
    const {
      columns: queryColumns,
      columnsType,
      date,
      status: columnsStatus,
      groupBy: queryGroupBy,
      from,
      to,
      filters: queryFilters,
      workspaceIds,
      orderBy,
      orderDir,
      currency,
    } = queryConditions;

    const rangeCollection = getRangeValueCollection({ timezone });

    form.value = {
      ...defaultForm(),
      name: props.data?.action === 'duplicate' ? `${name} (${t('____x.copy')})` : name,
      availableUntil: availableUntil
        ? formatDateLocal(availableUntil)
        : defaultForm().availableUntil,
      disableAvailableUntil: !availableUntil,
      columnsType,
      status,
      columnsStatus,
      groupBy: queryGroupBy,
      ...(date && date !== 'customDateTime'
        ? {
            reportPeriodStart: rangeCollection[date as keyof typeof rangeCollection].from,
            reportPeriodEnd: rangeCollection[date as keyof typeof rangeCollection].to,
          }
        : {
            reportPeriodStart: from,
            reportPeriodEnd: to,
          }),
      timezone,
      created,
      date: date || 'customDateTime',
      updated,
      updatedBy,
      filters: queryFilters,
      showDate,
      workspaceIds,
      orderBy,
      orderDir,
      currency: currency || store.user.currencyId,
      ...reportFilterConditions(queryConditions),
    };

    setSelectedColumns(queryColumns);
  } else {
    const initialFilters = getFiltersFromApi();
    const { status: columnsStatus, orderBy, orderDir } = initialFilters;

    const savedColumns = reportParamsApi.savedColumnsInCurentPreset.value;
    const reportParams = reportParamsApi.getReportParams();
    const { from, to, timezone, currency, date } = reportParams || {};
    const { selectedWorkspaces } = store.user;
    const selectedColumnsFromPreset = savedColumns[storeColumns.value as string].columns as string[];

    form.value = {
      ...defaultForm(),
      columnsType: storeColumns.value as string,
      reportPeriodStart: from as string,
      reportPeriodEnd: to as string,
      timezone: timezone as string,
      status: 'active',
      columnsStatus: columnsStatus as string,
      date: date as string,
      groupBy: groupBy.value,
      orderBy: orderBy as string,
      orderDir: orderDir as string,
      currency: (currency as string) || store.user.currencyId,
      workspaceIds: selectedWorkspaces,
      filters: filters.value,
      ...reportFilterConditions(reportParams),
    };

    setSelectedColumns(selectedColumnsFromPreset);
  }

  selectAll.value = selectedColumns.value.length === Object.keys(columns.value).length;
  searchColumn.value = '';
  active.value = true;

  nextTick(() => {
    oldHash.value = getCurrentHash();
  });
});
</script>


<style lang="scss" scoped>
  .tooltip-container {
    padding: 7px;
    border-radius: 5px;

    .info-text {
      color: var(--color-graphite);
    }
  }

  .tooltip-content {
    height: 100%;
    cursor: default;

    .label {
      text-transform: capitalize;
      color: var(--color-green);
      border-bottom: 1px dashed;
    }
  }

  .columns-container {
    height: 100%;
    min-height: 200px;
    display: flex;
    flex-direction: column;
  }

  .form-container {
    display: flex;
    flex-direction: column;
    overflow:auto;
    padding:24px;
    height: 100%;
    width: 100%;
  }

  .columns-wrapper {
    margin-top: 5px;
    margin-left: -8px;
  }

  .not-found {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .columns {
    height: 300px;
  }

  .workspaces-columns{
    max-height: 200px!important;
  }

  .workspaces-columns,
  .columns {
    padding-bottom: 5px;
    padding-left: 8px;
    grid-gap: 5px 10px;
    display: grid;
    overflow-y: auto;
    grid-template-columns: repeat(2, minmax(100px, 1fr));

    @media (max-width: 550px) {
      grid-template-columns: 100%;
    }

    .cell {
      word-break: break-all;
      :deep(.v-input--selection-controls__input) {
        align-items: flex-start;
        top: 1px;
      }

      :deep(.v-input__slot) {
        align-items: flex-start;
      }

      :deep(.v-label) {
        display: block;
        top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      :deep(.v-input__control) {
        width: 100%;
      }
    }
  }

  .period-picker {
    margin: 0 0 15px;
    width: 100%;
  }

  .ttl {
    display: flex;
    align-items: start;
    margin-top: 4px;

    .input {
      width: 65px;
      :deep(input) {
        padding: 0 15px!important;
      }
    }

    .select {
      margin-left: 5px;
      width: 100px;
    }
  }

  .report-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header {
    display: flex;
    position: relative;
    align-items: center;
  }

  .label {
    font-size: 13px;
    color: var(--color-graphite);
  }

  .show-columns {
    font-size: 13px;
    font-weight: bold;
    color: var(--color-graphite);
  }

  .disable-available-until{
    margin-bottom: -10px;
  }
</style>
